import { useEffect, useState, SyntheticEvent } from "react";
import MenuItem from "@mui/material/MenuItem";
import Grid from "@mui/material/Grid";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import FieldError from "shared/organisms/dynamicform/FieldError";
import { Controller } from "react-hook-form";
import { ISelectFieldProps } from "./types";
import DoNotDisturbIcon from "@mui/icons-material/DoNotDisturb";
import { LoaderBar, LoaderWrapper, StyledSelect } from "./styles";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import request from "utils/axios/service";
import { metadataSuccess } from "app-component/slices/metadata-slice";
import { useLazyQuery } from "@apollo/client";
import { GET_COURSES_LIST } from "utils/graphql/services/global-content-hierarchy/queries";
import entitesClient from "utils/graphql/entitiesClient";
import { SearchField } from "./searchField";
import { isEmpty } from "utils/helpers";
import { Typography } from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";

const NormalSelectSearchOnly = ({
  field,
  control,
  errors,
  setValue,
  getValues,
  watch,
}: ISelectFieldProps) => {
  const [options, setOptions] = useState([]);
  const [searchField, setSearchField] = useState("");
  const [searchOptions, setSearchOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState<any>({});
  const [triggerOptionList, setTriggerOptionList] = useState(false);
  const { data: metaData, updatedAt } = useAppSelector(
    (state) => state.metadataReducer
  );

  const [getData, { loading, data }] = useLazyQuery(field.query, {
    client: entitesClient,
    fetchPolicy: "no-cache",
  });

  const dispatch = useAppDispatch();

  const convertToLabelValueFormat = (field: any, data) => {
    if (!data || data.length === 0) return [];
    return data.map((item: any) => ({
      label: item[field.keyValue.label],
      value: item[field.keyValue.value],
    }));
  };
  const updateMetaData = (listData) => {
    let metadataList = { ...metaData };
    metadataList[field.list] = listData;
    dispatch(metadataSuccess(metadataList));
  };
 
  const filterByMandatoryIds = (records: any[]) => {
    if (!field?.mandatoryIds || field.mandatoryIds.length === 0) {
      return records;
    }
    return records.filter((record: any) => {
      for (const mandatoryIdField of field.mandatoryIds) {
        const fieldValue = record[mandatoryIdField];
        if (fieldValue=== null || fieldValue === "" || fieldValue === undefined) {
          return false;
        }
      }
      return true;
    });
  };

  const getGQLData = async (searchString) => {
    const searchKey = field?.searchFieldKey;
    const gqlKey = field?.gqlKey;
    const call1: any = await getData({
      variables: { filter: { [searchKey]: searchString, limit: 50, page: 1 } },
    })
      .then((res) => {
        const records = res?.data?.[gqlKey]?.records;
        if (!isEmpty(records)) {
          const filteredRecords = filterByMandatoryIds(records);
          updateMetaData(filteredRecords);
        } else {
          updateMetaData([]);
        }
      })
      .catch(() => {
        updateMetaData([]);
      });
  };

  useEffect(() => {
    if (metaData) {
      const jsonMetaData = metaData;
      const temp = jsonMetaData[field.list];
      let opts: any = [];
      if (temp && temp.length > 0 && !field.onlyApiCall) {
        opts = temp.map((item: any) => ({
          label: item[field.keyValue.label],
          value: item[field.keyValue.value],
        }));
        setOptions(opts);
      }
    }
  }, [field, updatedAt]);

  useEffect(() => {
    updateMetaData([]);
    setOptions([]);
  }, [triggerOptionList]);

  const handleChange = (e: any, onChange) => {
     const val = e.target.value;
    if (isEmpty(val)) {
      onChange(val);
      setSelectedOption({});
    } else {
      onChange(val);
      const selectedObj = options.find((opt: any) => opt.value === val);
      setSelectedOption(selectedObj || {});
    }
  };

  const handleSearch = async (event) => {
    const searchString: string = event.target.value.toLowerCase();
    setSearchField(event.target.value);
    if (searchString?.length > 2) {
      getGQLData(searchString);
    } else {
      updateMetaData([]);
      setOptions([]);
    }
  };

  const hasOptions = options && options.length > 0;
  return (
    <Grid item {...field.breakpoints}>
      <Controller
        render={({ field: { onChange, value } }) => (
          <>
            <FormControl
              sx={{ width: "100%" }}
              error={errors && errors[field.id]?.message}
              size="small"
            >
              <InputLabel id="demo-simple-select-label">
                {field.label}
              </InputLabel>
              <StyledSelect
                $readOnly={field.readOnly}
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                value={value || ""}
                label={field.label}
                className={field.className}
                required={field.required}
                disabled={field.disabled}
                 renderValue={(selected) => {
                  const option:any = options.find((opt:any) => opt.value === selected);
                  return option ? option.label : !isEmpty(selectedOption)?(selectedOption?.label) : "";
                }}
                inputProps={{
                  readOnly: field.readOnly ? field.readOnly : false,
                }}
                onChange={(e) => handleChange(e, onChange)}
                MenuProps={{
                  sx: {
                    "&& .MuiList-root": {
                      display: "flex",
                      flexDirection: "column",
                      padding: "10px",
                    },
                  },
                }}
              >
                {!isEmpty(selectedOption) && !isEmpty(value) && (
                  <MenuItem
                    value={selectedOption?.value}
                    key={selectedOption?.value}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                    }}
                  >
                    <Typography >
                      {selectedOption?.label}
                    </Typography>
                    <CancelIcon
                      fontSize="small"
                      sx={{
                        cursor: "pointer",
                        color: "text.secondary",
                        "&:hover": { color: "error.main" },
                      }}
                      onClick={(e) => {
                        e.stopPropagation(); // prevent menu from closing when clicking the icon
                        setSelectedOption({});
                        setValue(field.id, ""); // clears the form value (react-hook-form)
                      }}
                    />
                  </MenuItem>
                )}

                <SearchField
                  handleSearch={handleSearch}
                  field={field}
                  setSearchField={setSearchField}
                  searchField={searchField}
                  triggerOptionList={triggerOptionList}
                  setTriggerOptionList={setTriggerOptionList}
                  setSearchOptions={setSearchOptions}
                />
                {loading && (
                  <LoaderWrapper>
                    <LoaderBar />
                    <Typography variant="body2" color="text.secondary" mt={1}>
                      Loading...
                    </Typography>
                  </LoaderWrapper>
                )}
                {!hasOptions && <MenuItem disabled>No options found</MenuItem>}
                {hasOptions && !field?.hideNone && (
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                )}
                {hasOptions &&
                  options.map((option: any) => {
                    return (
                      <MenuItem value={option.value} key={option.value}>
                        {option.label}
                      </MenuItem>
                    );
                  })}
              </StyledSelect>
            </FormControl>

            {field.showError ? (
              <FieldError field={field} errors={errors} />
            ) : (
              field.customizableError
            )}
          </>
        )}
        name={field.id}
        control={control}
        defaultValue=""
      />
      {errors && errors[field.id]?.message && (
        <FieldError field={field} errors={errors} />
      )}
    </Grid>
  );
};

export default NormalSelectSearchOnly;
